import { saveBlob } from '@/lib/download';

/**
 * Writing a register as a real Excel workbook, in the browser.
 *
 * The companion of `downloadCsv`: the rows are already authorised and already
 * in the page, so the file is built here rather than asked of a new endpoint.
 * A CSV is not what an office means by "Excel" — it opens with every column
 * the same width, admission numbers like `00123` lose their zeros, and there
 * is no header row to freeze — so this writes the `.xlsx` itself.
 *
 * No library: an `.xlsx` is a zip of a handful of XML parts, and the subset a
 * flat register needs (one sheet, a bold frozen header, text and numbers) is
 * small enough to own outright. The zip is written uncompressed ("stored"),
 * which every spreadsheet reads and which needs nothing beyond a CRC.
 *
 * Every text value is written as an inline string, never as a formula, so a
 * name beginning `=` or `+` cannot run when the file is opened — the escaping
 * `downloadCsv` has to do by hand is not needed here.
 */

export type XlsxCell = string | number | null;

/** Excel's own ceiling on one cell; anything longer will not open. */
const MAX_CELL_LENGTH = 32_767;

/** Excel refuses a sheet name longer than this, or carrying any of these. */
const MAX_SHEET_NAME = 31;
const SHEET_NAME_FORBIDDEN = /[[\]:*?/\\]/g;

/** Column widths in characters — wide enough to read, never a whole screen. */
const MIN_COLUMN_WIDTH = 8;
const MAX_COLUMN_WIDTH = 60;

/** Saves `rows` as a one-sheet workbook. The first row is the header. */
export function downloadXlsx(fileName: string, sheetName: string, rows: XlsxCell[][]): void {
  saveBlob(buildXlsx(sheetName, rows), fileName);
}

export function buildXlsx(sheetName: string, rows: XlsxCell[][]): Blob {
  const encoder = new TextEncoder();
  const parts: [string, string][] = [
    ['[Content_Types].xml', CONTENT_TYPES],
    ['_rels/.rels', ROOT_RELS],
    ['xl/workbook.xml', workbookXml(sheetName)],
    ['xl/_rels/workbook.xml.rels', WORKBOOK_RELS],
    ['xl/styles.xml', STYLES],
    ['xl/worksheets/sheet1.xml', sheetXml(rows)],
  ];

  return new Blob([zipStored(parts.map(([name, xml]) => ({ name, data: encoder.encode(xml) })))], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/* ---------------------------------------------------------------- XML parts */

const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const MAIN_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const REL_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

const CONTENT_TYPES =
  `${XML_HEAD}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
  '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
  '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
  '</Types>';

const ROOT_RELS =
  `${XML_HEAD}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
  `<Relationship Id="rId1" Type="${REL_NS}/officeDocument" Target="xl/workbook.xml"/>` +
  '</Relationships>';

const WORKBOOK_RELS =
  `${XML_HEAD}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
  `<Relationship Id="rId1" Type="${REL_NS}/worksheet" Target="worksheets/sheet1.xml"/>` +
  `<Relationship Id="rId2" Type="${REL_NS}/styles" Target="styles.xml"/>` +
  '</Relationships>';

/** Two cell styles: `s="0"` the default, `s="1"` the bold header. */
const STYLES =
  `${XML_HEAD}<styleSheet xmlns="${MAIN_NS}">` +
  '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>' +
  '<font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
  '<fills count="2"><fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  '</styleSheet>';

function workbookXml(sheetName: string): string {
  const name =
    sheetName.replace(SHEET_NAME_FORBIDDEN, ' ').trim().slice(0, MAX_SHEET_NAME) || 'Sheet1';

  return (
    `${XML_HEAD}<workbook xmlns="${MAIN_NS}" xmlns:r="${REL_NS}">` +
    `<sheets><sheet name="${escapeXml(name)}" sheetId="1" r:id="rId1"/></sheets>` +
    '</workbook>'
  );
}

function sheetXml(rows: XlsxCell[][]): string {
  const columnCount = Math.max(0, ...rows.map((row) => row.length));

  // Sized to the longest value in each column, so the file opens readable
  // rather than as a wall of `####` and clipped names.
  const widths = Array.from({ length: columnCount }, (_, column) => {
    const longest = Math.max(0, ...rows.map((row) => cellText(row[column] ?? null).length));
    return Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, longest + 2));
  });

  const cols = widths
    .map(
      (width, index) =>
        `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`,
    )
    .join('');

  const body = rows
    .map((row, rowIndex) => {
      const style = rowIndex === 0 ? ' s="1"' : '';
      const cells = row
        .map((value, column) => {
          const ref = `${columnLetters(column)}${rowIndex + 1}`;
          if (value === null || value === '') return '';
          if (typeof value === 'number' && Number.isFinite(value)) {
            return `<c r="${ref}"${style}><v>${value}</v></c>`;
          }
          return `<c r="${ref}"${style} t="inlineStr"><is><t xml:space="preserve">${escapeXml(cellText(value))}</t></is></c>`;
        })
        .join('');
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join('');

  // The header stays in view however far down the register somebody scrolls.
  const frozenHeader =
    rows.length > 1
      ? '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>'
      : '';

  return (
    `${XML_HEAD}<worksheet xmlns="${MAIN_NS}" xmlns:r="${REL_NS}">` +
    `<sheetViews><sheetView workbookViewId="0">${frozenHeader}</sheetView></sheetViews>` +
    '<sheetFormatPr defaultRowHeight="15"/>' +
    (cols ? `<cols>${cols}</cols>` : '') +
    `<sheetData>${body}</sheetData>` +
    '</worksheet>'
  );
}

function cellText(value: XlsxCell): string {
  return value === null ? '' : String(value).slice(0, MAX_CELL_LENGTH);
}

/** 0 → A, 25 → Z, 26 → AA. */
function columnLetters(index: number): string {
  let letters = '';
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters;
  }
  return letters;
}

/**
 * Escaped for an XML text node or attribute. Control characters are dropped
 * rather than escaped: XML 1.0 has no way to carry them, and one stray tab
 * pasted into a name would otherwise make Excel refuse the whole file.
 */
function escapeXml(text: string): string {
  return text
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

/* ---------------------------------------------------------------- zip */

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

/** A zip with every entry stored as-is — the "no compression" method 0. */
function zipStored(entries: ZipEntry[]): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder();
  const { time, date } = dosDateTime(new Date());

  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const crc = crc32(entry.data);
    const size = entry.data.length;

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0, true);
    local.setUint16(8, 0, true);
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, size, true);
    local.setUint32(22, size, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    locals.push(new Uint8Array(local.buffer), name, entry.data);

    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true);
    central.setUint16(4, 20, true);
    central.setUint16(6, 20, true);
    central.setUint16(8, 0, true);
    central.setUint16(10, 0, true);
    central.setUint16(12, time, true);
    central.setUint16(14, date, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, size, true);
    central.setUint32(24, size, true);
    central.setUint16(28, name.length, true);
    central.setUint32(42, offset, true);
    centrals.push(new Uint8Array(central.buffer), name);

    offset += 30 + name.length + size;
  }

  const centralSize = centrals.reduce((sum, part) => sum + part.length, 0);

  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, entries.length, true);
  end.setUint16(10, entries.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);

  return concat([...locals, ...centrals, new Uint8Array(end.buffer)]);
}

function concat(parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

/** The zip format's MS-DOS timestamp: two-second resolution, local time. */
function dosDateTime(when: Date): { time: number; date: number } {
  return {
    time: (when.getHours() << 11) | (when.getMinutes() << 5) | Math.floor(when.getSeconds() / 2),
    date: ((when.getFullYear() - 1980) << 9) | ((when.getMonth() + 1) << 5) | when.getDate(),
  };
}

let crcTable: Uint32Array | null = null;

function crc32(data: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
      let c = n;
      for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }

  let crc = 0xffffffff;
  for (const byte of data) crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}
